import TspApiService from "../../../api/tspApiService";
import { DEFAULT_TRACKING_CONFIG, fixFromPosition, shouldQueueFix } from "./trackingModel";

// This device's live GPS tracker — one per browser tab, deliberately NOT tied to
// a React component: it keeps tracking while the user moves between TSP pages
// (e.g. from the journey to its Live Location map), and React screens only
// subscribe to it (useTspGpsTracker).
//
// Flow: start(journeyId) -> navigator.geolocation.watchPosition (the browser asks
// for permission) -> the first fix creates / resumes the server session ->
// fixes are throttled into a queue -> the queue is sent every
// flush_interval_seconds, and an empty batch (heartbeat) at least every
// heartbeat_seconds -> stop() clears the watch, sends what is left and switches
// the server session off. Saved history is never deleted.
//
// Honest status: `phase` changes only on what actually happened — a fix, a
// Geolocation error, a failed request, a permission change, going offline, or
// no FIRST fix within no_fix_after_seconds. Once there is a fix, time without a
// new one is NOT treated as "no signal": browsers only report changes, so a
// party standing still gets no callbacks and no errors. The device's own GPS
// state goes with every batch (gps), and the server independently marks a
// session "signal lost" when heartbeats stop (tab closed, phone asleep).
//
// A web page cannot switch the device's GPS hardware on or off: "Enable GPS"
// asks the browser for location access; if the device's location service is
// off, the Geolocation API reports it unavailable and so do we.
const WATCH_OPTIONS = Object.freeze({ enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 });
// Many browsers stop calling watchPosition while the device stands still. After
// half of no_fix_after_seconds without a fix, one fresh reading is asked for, so
// "standing still" is not mistaken for "no GPS signal".
const PROBE_OPTIONS = Object.freeze({ enableHighAccuracy: true, maximumAge: 0, timeout: 20000 });
const TICK_MS = 5000;
const MAX_QUEUE = 1000; // ~ several hours offline at walking pace; oldest dropped beyond
const REARM_MS = 30000; // re-arm the watch this often while location is unavailable

const INITIAL = Object.freeze({ journeyId: null, phase: "off", message: "", lastFix: null, lastSentAt: null, pending: 0, sessionId: null });

const defaultDeps = () => ({
  api: TspApiService.tracking,
  geolocation: typeof navigator !== "undefined" ? navigator.geolocation : undefined,
  permissions: typeof navigator !== "undefined" ? navigator.permissions : undefined,
  isOnline: () => (typeof navigator === "undefined" || navigator.onLine !== false),
  isSecureContext: () => typeof window === "undefined" || window.isSecureContext !== false,
  win: typeof window !== "undefined" ? window : undefined,
  doc: typeof document !== "undefined" ? document : undefined,
  now: () => Date.now(),
});

const errorCode = (error) => error?.response?.data?.details?.code;

// This device's GPS state as reported to the server with each batch.
const DEVICE_STATE = { unavailable: "UNAVAILABLE", searching: "NO_SIGNAL" };
const deviceState = (phase) => DEVICE_STATE[phase] || "OK";

export function createGpsTracker(overrides = {}) {
  const deps = { ...defaultDeps(), ...overrides };
  const listeners = new Set();
  let state = { ...INITIAL };
  let config = { ...DEFAULT_TRACKING_CONFIG };
  let watchId = null;
  let timer = null;
  let queue = [];
  let lastQueued = null;
  let lastFixAt = 0;
  let lastSentAt = 0;
  let lastSentFixT = 0; // timestamp of the newest fix the server has
  let probing = false;
  let startedAt = 0;
  let lastRearmAt = 0;
  let sending = false;
  let sessionPromise = null;
  let permissionStatus = null;
  let generation = 0; // bumps on every start/teardown: late callbacks of an old run are ignored

  const emit = (patch) => {
    state = { ...state, ...patch, pending: queue.length };
    listeners.forEach((listener) => listener(state));
  };

  const isActive = () => watchId !== null;

  function onOnline() {
    if (!isActive()) return;
    emit({ phase: lastFixAt ? "on" : "starting", message: "" });
    flush();
  }
  function onOffline() {
    if (isActive()) emit({ phase: "offline", message: "No internet connection. Locations are kept on this device and sent when you are back online." });
  }
  function onVisibility() {
    if (!isActive()) return;
    tick();
    flush(true);
  }
  function onPermissionChange() {
    if (permissionStatus && permissionStatus.state === "denied" && isActive()) denied();
  }

  function listen(on) {
    const method = on ? "addEventListener" : "removeEventListener";
    deps.win?.[method]("online", onOnline);
    deps.win?.[method]("offline", onOffline);
    deps.doc?.[method]("visibilitychange", onVisibility);
    if (!on && permissionStatus) {
      permissionStatus.removeEventListener?.("change", onPermissionChange);
      permissionStatus = null;
    }
    if (on && deps.permissions?.query) {
      const run = generation;
      deps.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          if (run !== generation || !isActive()) return;
          permissionStatus = status;
          status.addEventListener?.("change", onPermissionChange);
        })
        .catch(() => {}); // Permissions API not available for geolocation (e.g. older Safari): Geolocation errors still tell us
    }
  }

  function teardown() {
    generation += 1;
    if (watchId !== null && deps.geolocation) deps.geolocation.clearWatch(watchId);
    watchId = null;
    if (timer) clearInterval(timer);
    timer = null;
    listen(false);
  }

  // Stops locally and forgets the session (the server already stopped it, or can't take more).
  function end(phase, message) {
    teardown();
    queue = [];
    sessionPromise = null;
    emit({ phase, message, sessionId: null });
  }

  function watch() {
    const run = generation;
    watchId = deps.geolocation.watchPosition(
      (position) => run === generation && onPosition(position),
      (error) => run === generation && onError(error),
      WATCH_OPTIONS
    );
    lastRearmAt = deps.now();
  }

  function ensureSession() {
    if (state.sessionId) return Promise.resolve(state.sessionId);
    if (sessionPromise) return sessionPromise;
    const run = generation;
    const journeyId = state.journeyId;
    sessionPromise = deps.api
      .start(journeyId)
      .then((response) => {
        if (run !== generation) return null;
        const sessionId = response.data.session.session_id;
        emit({ sessionId, message: "" });
        return sessionId;
      })
      .catch((error) => {
        if (run !== generation) return null;
        const status = error?.response?.status;
        if (status === 409) end("ended", error.response.data?.message || "This journey is not in progress, so it cannot be tracked.");
        else if (status === 404 || status === 403) end("error", "You do not have access to track this journey.");
        else emit({ message: "Could not reach the server yet — locations are kept and will be sent." }); // retried on the next tick
        return null;
      })
      .finally(() => {
        if (run === generation) sessionPromise = null;
      });
    return sessionPromise;
  }

  function onPosition(position) {
    const now = deps.now();
    const fix = fixFromPosition(position, now);
    lastFixAt = now;
    if (shouldQueueFix(fix, lastQueued, config)) {
      queue.push(fix);
      if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
      lastQueued = fix;
    }
    const firstFix = !state.lastFix || state.phase === "starting";
    emit({ lastFix: fix, phase: deps.isOnline() ? "on" : "offline", message: deps.isOnline() ? "" : state.message });
    if (firstFix) flush(true); // show up on the live map straight away
  }

  function onError(error) {
    if (error.code === 1) return denied();
    if (error.code === 2) {
      return emit({ phase: "unavailable", message: "Your device could not determine its location. Turn on location services (GPS) and check the signal." });
    }
    // 3 = TIMEOUT: no fix in time; the watch keeps trying.
    return emit({ phase: "searching", message: "Waiting for a GPS fix. Move to an open area if this continues." });
  }

  async function denied() {
    const { journeyId, sessionId } = state;
    teardown();
    emit({ phase: "denied", message: "Location access is blocked for this site. Allow location in your browser settings, then press Enable GPS again." });
    if (sessionId) {
      await sendQueue(journeyId, sessionId).catch(() => {});
      deps.api.stop(journeyId, { session_id: sessionId, reason: "PERMISSION_DENIED" }).catch(() => {});
    }
    queue = [];
    emit({ sessionId: null });
  }

  // Sends the next batch. A heartbeat (nothing queued) still carries the newest
  // fix when the server does not have it yet, so a device standing still keeps
  // its "current location" fresh without adding route points.
  async function sendQueue(journeyId, sessionId) {
    let batch = queue.slice(0, config.max_batch_points);
    const fromQueue = batch.length;
    if (!fromQueue && state.lastFix && state.lastFix.t > lastSentFixT) batch = [state.lastFix];
    await deps.api.sendPoints(journeyId, sessionId, batch, deviceState(state.phase));
    queue.splice(0, fromQueue);
    lastSentAt = deps.now();
    batch.forEach((fix) => {
      lastSentFixT = Math.max(lastSentFixT, fix.t);
    });
    return batch.length;
  }

  async function flush(force = false) {
    if (!isActive() || sending) return;
    const now = deps.now();
    const due = force || (queue.length && now - lastSentAt >= config.flush_interval_seconds * 1000) || now - lastSentAt >= config.heartbeat_seconds * 1000;
    if (!due) return;
    if (!deps.isOnline()) {
      onOffline();
      return;
    }
    if (!state.sessionId) {
      if (lastFixAt) await ensureSession();
      if (!state.sessionId) return;
    }
    const run = generation;
    const { journeyId, sessionId } = state;
    sending = true;
    try {
      await sendQueue(journeyId, sessionId);
      if (run !== generation) return;
      const recovered = state.phase === "offline" ? (lastFixAt ? "on" : "starting") : state.phase;
      emit({ lastSentAt, phase: recovered, message: recovered === state.phase ? state.message : "" });
      if (queue.length >= config.max_batch_points) setTimeout(() => flush(true), 0); // catching up after being offline
    } catch (error) {
      if (run !== generation) return;
      const status = error?.response?.status;
      const code = errorCode(error);
      if (status === 409 && code === "JOURNEY_NOT_ACTIVE") end("ended", "This journey is no longer in progress, so tracking has stopped.");
      else if (status === 409) end("off", "Tracking was switched off for this journey (for example, it was started on another journey).");
      else if (status === 404 || status === 403) end("error", "You no longer have access to track this journey.");
      else if (status === 400) {
        queue.splice(0, config.max_batch_points); // a malformed batch would never succeed: drop it rather than block the queue
      } else {
        emit({ phase: "offline", message: "Could not reach the server. Locations are kept on this device and will be sent automatically." });
      }
    } finally {
      sending = false;
    }
  }

  function tick() {
    if (!isActive()) return;
    const now = deps.now();
    const noFixFor = now - (lastFixAt || startedAt);
    if (lastFixAt && !probing && noFixFor > (config.no_fix_after_seconds * 1000) / 2 && deps.geolocation.getCurrentPosition) {
      probing = true;
      const run = generation;
      deps.geolocation.getCurrentPosition(
        (position) => {
          probing = false;
          if (run === generation && isActive()) onPosition(position);
        },
        () => {
          probing = false; // the watch's own error callback reports the reason
        },
        PROBE_OPTIONS
      );
    }
    if (state.phase === "starting" && noFixFor > config.no_fix_after_seconds * 1000) {
      emit({ phase: "searching", message: "No GPS fix yet. Check that location services are on and move to an open area." });
    }
    if (state.phase === "unavailable" && now - lastRearmAt > REARM_MS) {
      // Some browsers end a watch after "position unavailable": ask again.
      deps.geolocation.clearWatch(watchId);
      watch();
    }
    flush();
  }

  function start(journeyId, serverConfig) {
    const id = String(journeyId);
    if (isActive() && state.journeyId === id) return;
    if (isActive()) teardown(); // another journey: the server stops that session when this one starts
    config = { ...DEFAULT_TRACKING_CONFIG, ...(serverConfig || {}) };
    queue = [];
    lastQueued = null;
    lastFixAt = 0;
    lastSentAt = 0;
    lastSentFixT = 0;
    probing = false;
    sessionPromise = null;
    if (!deps.geolocation || !deps.isSecureContext()) {
      emit({ ...INITIAL, journeyId: id, phase: "unsupported", message: "This browser cannot share location (it needs a secure https:// page with location support)." });
      return;
    }
    generation += 1;
    startedAt = deps.now();
    emit({ ...INITIAL, journeyId: id, phase: "starting", message: "" });
    watch();
    timer = setInterval(tick, TICK_MS);
    listen(true);
  }

  async function stop() {
    if (!isActive()) {
      // "Paused" = still ON on the server but not running here: switch it off there too.
      if (state.phase === "paused" && state.journeyId) await deps.api.stop(state.journeyId, { reason: "USER" }).catch(() => {});
      if (state.phase !== "off") emit({ phase: "off", message: "" });
      return;
    }
    const { journeyId, sessionId } = state;
    teardown();
    emit({ phase: "off", message: "" });
    if (sessionId) {
      await sendQueue(journeyId, sessionId).catch(() => {});
      await deps.api.stop(journeyId, { session_id: sessionId, reason: "USER" }).catch(() => {
        // Offline: the server marks the session "signal lost" on its own; the next Enable resumes it.
      });
    }
    queue = [];
    emit({ sessionId: null });
  }

  // On opening a journey: the server says this user's tracking is still ON for
  // it (e.g. after a page reload). Resume silently when location permission is
  // already granted; otherwise show "paused" and let the user press Enable —
  // never prompt for permission without a click.
  async function resumeIfActive(journeyId, me, serverConfig) {
    const id = String(journeyId);
    if (isActive() || !me || me.status !== "ACTIVE") return;
    let granted = false;
    try {
      granted = deps.permissions?.query ? (await deps.permissions.query({ name: "geolocation" })).state === "granted" : false;
    } catch (error) {
      granted = false;
    }
    if (isActive()) return;
    if (granted) start(id, serverConfig);
    else emit({ ...INITIAL, journeyId: id, phase: "paused", message: "Tracking is on for this journey but paused on this device. Press Enable GPS to continue." });
  }

  return {
    start,
    stop,
    resumeIfActive,
    isTracking: (journeyId) => isActive() && state.journeyId === String(journeyId),
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    // Tests only.
    _tick: tick,
    _flush: flush,
  };
}

const gpsTracker = createGpsTracker();
export default gpsTracker;
